/**
 * Compact card for configuring Decision research web search (Brave / Tavily / Serper).
 * Used on Platform Admin AI Settings and Tenant AI Settings.
 * Never displays or echoes the raw API key after save.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '@/services/api';
import { useLanguage } from '@/state/language/LanguageProvider';

export type ResearchSearchProvider = 'brave' | 'tavily' | 'serper';

export interface ResearchSearchSettingsView {
  provider: ResearchSearchProvider | null;
  apiKeyConfigured: boolean;
  effectiveSource: 'tenant' | 'platform' | 'env' | 'none';
  envFallbackAvailable: boolean;
}

interface ResearchSearchSettingsCardProps {
  /** Platform admin endpoint vs tenant endpoint */
  mode: 'platform' | 'tenant';
  className?: string;
}

const PROVIDER_OPTIONS: { value: ResearchSearchProvider; labelKey: string; fallback: string }[] = [
  { value: 'brave', labelKey: 'aiSettings.researchSearch.providerBrave', fallback: 'Brave Search' },
  { value: 'tavily', labelKey: 'aiSettings.researchSearch.providerTavily', fallback: 'Tavily' },
  { value: 'serper', labelKey: 'aiSettings.researchSearch.providerSerper', fallback: 'Serper' },
];

function endpointFor(mode: 'platform' | 'tenant'): string {
  return mode === 'platform' ? '/ai/admin/research-search' : '/ai/config/research-search';
}

export const ResearchSearchSettingsCard: React.FC<ResearchSearchSettingsCardProps> = ({
  mode,
  className = '',
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<ResearchSearchSettingsView | null>(null);
  const [provider, setProvider] = useState<ResearchSearchProvider | ''>('');
  const [apiKey, setApiKey] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<ResearchSearchSettingsView>(endpointFor(mode));
      setView(res.data);
      setProvider(res.data.provider ?? '');
      setApiKey('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(
        err.response?.data?.message ||
          t('aiSettings.researchSearch.loadError') ||
          'Failed to load research search settings'
      );
    } finally {
      setLoading(false);
    }
  }, [mode, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!provider) {
      toast.error(
        t('aiSettings.researchSearch.providerRequired') || 'Select a search provider'
      );
      return;
    }
    if (!apiKey.trim() && !view?.apiKeyConfigured) {
      toast.error(
        t('aiSettings.researchSearch.apiKeyRequired') || 'Enter an API key'
      );
      return;
    }
    try {
      setSaving(true);
      const body: { provider: ResearchSearchProvider; apiKey?: string } = {
        provider,
      };
      if (apiKey.trim()) body.apiKey = apiKey.trim();
      const res = await api.put<ResearchSearchSettingsView>(endpointFor(mode), body);
      setView(res.data);
      setApiKey('');
      toast.success(
        t('aiSettings.researchSearch.saved') || 'Research search settings saved'
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(
        err.response?.data?.message ||
          t('aiSettings.researchSearch.saveError') ||
          'Failed to save'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (
      !window.confirm(
        t('aiSettings.researchSearch.clearConfirm') ||
          'Remove the stored search API key for this scope?'
      )
    ) {
      return;
    }
    try {
      setSaving(true);
      const res = await api.put<ResearchSearchSettingsView>(endpointFor(mode), {
        clearApiKey: true,
        provider: provider || null,
      });
      setView(res.data);
      setApiKey('');
      toast.success(
        t('aiSettings.researchSearch.cleared') || 'Search API key cleared'
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(
        err.response?.data?.message ||
          t('aiSettings.researchSearch.saveError') ||
          'Failed to clear'
      );
    } finally {
      setSaving(false);
    }
  };

  const sourceLabel = (() => {
    if (!view) return '';
    switch (view.effectiveSource) {
      case 'tenant':
        return t('aiSettings.researchSearch.sourceTenant') || 'Using tenant key';
      case 'platform':
        return t('aiSettings.researchSearch.sourcePlatform') || 'Using platform key';
      case 'env':
        return t('aiSettings.researchSearch.sourceEnv') || 'Using environment key';
      default:
        return t('aiSettings.researchSearch.sourceNone') || 'Not configured';
    }
  })();

  return (
    <div className={`bg-white shadow rounded-lg p-4 mb-6 ${className}`}>
      <h2 className="text-base font-semibold text-gray-900 mb-1">
        {t('aiSettings.researchSearch.title') || 'Web search (research tools)'}
      </h2>
      <p className="text-sm text-gray-600 mb-3">
        {mode === 'platform'
          ? t('aiSettings.researchSearch.platformHint') ||
            'Platform default for Decision agent search (run.search). Tenants may override with their own key.'
          : t('aiSettings.researchSearch.tenantHint') ||
            'Optional tenant key for Decision agent search. If unset, platform then environment keys are used.'}
      </p>

      {loading ? (
        <p className="text-sm text-gray-500">
          {t('common.loading') || 'Loading…'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('aiSettings.researchSearch.provider') || 'Provider'}
              </label>
              <select
                value={provider}
                onChange={(e) =>
                  setProvider(e.target.value as ResearchSearchProvider | '')
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#004C94]"
                disabled={saving}
              >
                <option value="">
                  {t('aiSettings.researchSearch.providerPlaceholder') || 'Select…'}
                </option>
                {PROVIDER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey) || o.fallback}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('aiSettings.researchSearch.apiKey') || 'API key'}
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={
                  view?.apiKeyConfigured
                    ? t('aiSettings.researchSearch.apiKeyConfiguredPlaceholder') ||
                      '•••••••• (leave blank to keep)'
                    : t('aiSettings.researchSearch.apiKeyPlaceholder') ||
                      'Paste API key'
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#004C94]"
                disabled={saving}
              />
            </div>
          </div>

          <p className="text-xs text-gray-500 mb-3">
            {view?.apiKeyConfigured
              ? t('aiSettings.researchSearch.keyStored') || 'A key is stored for this scope.'
              : t('aiSettings.researchSearch.keyNotStored') ||
                'No key stored for this scope.'}{' '}
            <span className="font-medium text-gray-700">{sourceLabel}</span>
            {view?.envFallbackAvailable && view.effectiveSource !== 'env' ? (
              <span className="text-gray-400">
                {' '}
                ({t('aiSettings.researchSearch.envAlsoAvailable') ||
                  'env fallback also available'})
              </span>
            ) : null}
          </p>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-[#004C94] text-white rounded-md hover:bg-[#004C94]/90 disabled:opacity-50"
            >
              {saving
                ? t('common.saving') || 'Saving…'
                : t('aiSettings.researchSearch.save') || 'Save'}
            </button>
            {view?.apiKeyConfigured ? (
              <button
                type="button"
                onClick={() => void handleClear()}
                disabled={saving}
                className="px-3 py-1.5 text-sm text-red-700 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                {t('aiSettings.researchSearch.clear') || 'Clear key'}
              </button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};

export default ResearchSearchSettingsCard;
